import React, { useEffect, useState } from "react"
import { momentZ } from "config/moment"
import { Icon, Table2, Button } from "components"
import { Timeline } from "antd"
import { getAuditTrail, isAuditTrailLoading, selectAuditTrail } from "store/AuditTrail/auditTrail.slice"
import useAppDispatch from "hooks/useAppDispatch"
import { Entities } from "types/auditTrail.types"
import useAppSelector from "hooks/useAppSelector"
import { Drawer } from "antd"
import moment from "moment"
import { DATE_FORMATS } from "config/constants"
import { isArray, isObject, debounce } from "lodash"

interface Props {
	id: string | null | undefined
	name: Entities
	showLabel?: boolean
	showInSchedule?: boolean
	disabled?: boolean
}

const AuditTrail: React.FC<Props> = ({ id, name, showLabel = true, showInSchedule = false, disabled = false }) => {
	const dispatch = useAppDispatch()
	const logs = useAppSelector(selectAuditTrail(id || "", name))
	const loading = useAppSelector(isAuditTrailLoading(id || "", name))
	const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false)
	const toggleHistoryModal = () => setShowHistoryModal(!showHistoryModal)

	const debouncedFetchAuditTrail = debounce((entityId: string, entityName: Entities) => {
		dispatch(getAuditTrail(entityId, entityName))
	}, 3000)

	useEffect(() => {
		if (id) debouncedFetchAuditTrail(id, name)

		return () => {
			debouncedFetchAuditTrail.cancel()
		}
	}, [id])

	if (!showInSchedule && loading) return <Icon name="spinner" size={20} color="black" className="animate-spin" />
	if (!showInSchedule && !logs?.length) return <></>

	return (
		<>
			{showInSchedule ? (
				<Button disabled={disabled || !logs?.length} loading={loading} onClick={toggleHistoryModal}>
					Audit
				</Button>
			) : (
				<div className="flex cursor-pointer items-center" onClick={toggleHistoryModal}>
					{showLabel ? (
						<span>
							{"Last Updated on "}
							{momentZ(logs?.[0]?.createdAt).format("MMMM DD, YYYY")}
							{" at "}
							{momentZ(logs?.[0]?.createdAt).format("HH:mm")}
							{" by "}
							{logs?.[0]?.user}
						</span>
					) : undefined}
					<Icon name="history" color="black" size={18} className="ml-2" />
				</div>
			)}
			{logs?.length ? (
				<Drawer
					contentWrapperStyle={{ backgroundColor: "transparent" }}
					title="Change Logs"
					placement="right"
					onClose={() => setShowHistoryModal(false)}
					open={showHistoryModal}
					width={"45%"}
					rootStyle={{
						color: "rgb(229 229 229);",
					}}
					headerStyle={{
						background: "#1f2937",
					}}
					closeIcon={<Icon name="cross" color="white" size={18} />}
				>
					<div className="px-2">
						<Timeline>
							{logs
								.map(({ changes, ...rest }) => ({
									changes: changes.filter(
										change =>
											!(
												isObject(change.to) ||
												isObject(change.from) ||
												isArray(change.to) ||
												isArray(change.from)
											),
									),
									...rest,
								}))
								.map((log: any, index: number) => {
									return (
										<Timeline.Item color="#ababab" key={`history_log_${index}`}>
											<div className={"flex flex-col gap-2"}>
												<div className="flex items-center gap-2 font-semibold">
													Changes
													{" on "}
													{momentZ(log?.createdAt).format("MMMM DD, YYYY")}
													{" at "}
													{momentZ(log?.createdAt).format("HH:mm")}
													{" by "}
													{log?.user}
												</div>
												<Table2
													tableClassName="w-full table-fixed"
													columns={[
														{ label: "Property Name", key: "label" },
														{
															label: "Old Value",
															key: "from",
															render: ({ row }) => {
																const isEmpty = !row.from || row.from === ""
																const isDate =
																	typeof row.from === "string" &&
																	moment(row.from, moment.ISO_8601, true).isValid()
																const fromValue = isDate
																	? moment(row.from).format(
																			DATE_FORMATS.SHIFT_EXPECTED_TIME_FORMAT,
																	  )
																	: row.from

																if (isEmpty) {
																	return (
																		<div className="inline-block h-4 w-3 rounded-full bg-red-100" />
																	)
																}

																return (
																	<div className="whitespace-normal break-words rounded-md bg-red-100 p-2 text-left leading-5">
																		{typeof fromValue === "string" &&
																		fromValue.includes(",") ? (
																			fromValue
																				.split(",")
																				.map(
																					(val: string, idx: number, arr) => (
																						<span key={idx}>
																							{val.trim()}
																							{idx < arr.length - 1 &&
																								", "}
																						</span>
																					),
																				)
																		) : (
																			<span>{fromValue}</span>
																		)}
																	</div>
																)
															},
														},
														{
															label: "New Value",
															key: "to",
															render: ({ row }) => {
																const isEmpty = !row.to || row.to === ""
																const isDate =
																	typeof row.to === "string" &&
																	moment(row.to, moment.ISO_8601, true).isValid()
																const toValue = isDate
																	? moment(row.to).format(
																			DATE_FORMATS.SHIFT_EXPECTED_TIME_FORMAT,
																	  )
																	: row.to

																if (isEmpty) {
																	return (
																		<div className="inline-block h-4 w-3 rounded-full bg-green-100" />
																	)
																}

																return (
																	<div className="whitespace-normal break-words rounded-md bg-green-100 p-2 text-left leading-5">
																		{typeof toValue === "string" &&
																		toValue.includes(",") ? (
																			toValue
																				.split(",")
																				.map(
																					(val: string, idx: number, arr) => (
																						<span key={idx}>
																							{val.trim()}
																							{idx < arr.length - 1 &&
																								", "}
																						</span>
																					),
																				)
																		) : (
																			<span>{toValue}</span>
																		)}
																	</div>
																)
															},
														},
													]}
													rows={log.changes}
												/>
											</div>
										</Timeline.Item>
									)
								})}
						</Timeline>
					</div>
				</Drawer>
			) : undefined}
		</>
	)
}

export default AuditTrail
